/**
 * Merge the reads from a scan (a licence front, an ID's machine-readable zone,
 * a US barcode — any subset) into one identity, then map that to what the
 * hand-over's CaRGOS form can accept. Pure. Two decisions live here:
 *
 *  - The ID document NUMBER and its place are never prefilled: the operator
 *    types the number and it is erased once CaRGOS has the receipt. The scan
 *    may read a number, but it does not reach the form.
 *  - Places (birth, citizenship) are carried as NAMES to seed the existing
 *    PlacePicker; the operator resolves each to a TABELLA LUOGHI code. The scan
 *    never invents a code.
 */
import type { FieldSource, ScanField, ScannedIdentity } from './ocr-interface'
import type { NameReading } from './validate'

/** Exact beats check-digited beats OCR'd when two sources offer the same field. */
const SOURCE_RANK: Record<FieldSource, number> = { barcode: 3, mrz: 2, licence: 1, manual: 0 }

function better<T>(
  a: ScanField<T> | undefined,
  b: ScanField<T> | undefined,
): ScanField<T> | undefined {
  if (!a) return b
  if (!b) return a
  if (SOURCE_RANK[a.source] !== SOURCE_RANK[b.source]) {
    return SOURCE_RANK[a.source] > SOURCE_RANK[b.source] ? a : b
  }
  return a.confidence >= b.confidence ? a : b
}

/** Fields present on ScannedIdentity, merged one source against another. */
const FIELDS = [
  'surname',
  'givenNames',
  'birthDate',
  'birthPlace',
  'nationality',
  'documentType',
  'documentNumber',
  'licenceNumber',
  'licenceExpiry',
  'licenceIssued',
  'licenceAuthority',
  'licenceCategories',
  'issuingCountry',
] as const

export interface MergedScan {
  readonly identity: ScannedIdentity
  /** Each source's name, for validate's cross-check. */
  readonly names: readonly NameReading[]
}

/** Merge any number of per-source reads into one identity (best value wins). */
export function mergeScans(...parts: readonly ScannedIdentity[]): MergedScan {
  const identity: Record<string, unknown> = {}
  for (const key of FIELDS) {
    let best: ScanField<unknown> | undefined
    for (const part of parts) {
      best = better(best, part[key] as ScanField<unknown> | undefined)
    }
    if (best) identity[key] = best
  }
  const names: NameReading[] = parts
    .map((p) => ({
      surname: p.surname?.value,
      givenNames: p.givenNames?.value,
      source: (p.surname ?? p.givenNames)?.source ?? ('licence' as FieldSource),
    }))
    .filter((n) => n.surname || n.givenNames)
  return { identity: identity as ScannedIdentity, names }
}

/** What the scan offers the hand-over form; places are names to resolve. */
export interface ScanPrefill {
  readonly surname?: ScanField
  readonly givenNames?: ScanField
  readonly birthDate?: ScanField
  /** A place name to seed the PlacePicker — not a code. */
  readonly birthPlaceName?: ScanField
  /** Citizenship as a name/alpha-3 to seed the picker. */
  readonly citizenshipName?: ScanField
  readonly documentType?: ScanField
  readonly licenceNumber?: ScanField
}

/**
 * Map a merged identity to the form prefill. Deliberately omits the ID document
 * number and place (Decision 2). Licence expiry, categories and the issuing
 * authority are intentionally not mapped either — v1 keeps them off the counter
 * form; they are used only by validate and the category check.
 */
export function toPrefill(identity: ScannedIdentity): ScanPrefill {
  const prefill: Record<string, ScanField | undefined> = {
    surname: identity.surname,
    givenNames: identity.givenNames,
    birthDate: identity.birthDate,
    birthPlaceName: identity.birthPlace,
    citizenshipName: identity.nationality,
    documentType: identity.documentType,
    licenceNumber: identity.licenceNumber,
  }
  return Object.fromEntries(
    Object.entries(prefill).filter(([, v]) => v !== undefined),
  ) as ScanPrefill
}
